import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { managementApi, type Totals } from '../lib/api'
import { formatBytes, formatDate, toCurrentOrigin } from '../lib/format'
import { Alert, Badge, EmptyState, PageShell, ProgressBar, Spinner } from '../components/ui'

/**
 * Owner dashboard. Like the rest of the owner surface it carries no auth of its
 * own — whatever proxy fronts the deployment is what keeps strangers out.
 */
export function Home() {
  const query = useQuery({ queryKey: ['dropboxes'], queryFn: managementApi.list })

  return (
    <PageShell>
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Dropboxes</h1>
          <p className="mt-2 text-zinc-600 dark:text-zinc-400">
            Collection links you have created.
          </p>
        </div>
        <Link to="/new" className="btn-primary">
          New dropbox
        </Link>
      </header>

      {query.isPending && (
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <Spinner /> Loading…
        </div>
      )}

      {query.isError && (
        <Alert tone="error" title="Could not load dropboxes">
          {(query.error as Error).message}
        </Alert>
      )}

      {query.data && <StorageSummary totals={query.data.totals} />}

      {query.data && query.data.dropboxes.length === 0 && (
        <EmptyState title="No dropboxes yet" hint="Create one and send the link to your recipient." />
      )}

      <div className="space-y-3">
        {query.data?.dropboxes.map(({ dropbox, quota, phase, links }) => (
          <a
            key={dropbox.id}
            href={toCurrentOrigin(links.adminUrl)}
            className="card block p-5 transition-shadow hover:shadow-md"
          >
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold">{dropbox.title}</h2>
              <Badge tone={dropbox.status === 'ready' ? 'done' : 'neutral'}>
                {dropbox.status === 'ready' ? 'Submitted' : 'Awaiting'}
              </Badge>
              <Badge tone={phase === 'open' ? 'neutral' : phase === 'soft_closed' ? 'warn' : 'danger'}>
                {phase === 'open' ? 'Open' : phase === 'soft_closed' ? 'Uploads closed' : 'Expired'}
              </Badge>
            </div>
            <div className="mt-3 max-w-sm">
              <ProgressBar value={quota.maxBytes ? quota.usedBytes / quota.maxBytes : 0} />
            </div>
            <div className="mt-2 text-xs text-zinc-500">
              {formatBytes(quota.usedBytes)} of {formatBytes(quota.maxBytes)} · expires{' '}
              {formatDate(dropbox.hardDeadline)}
            </div>
          </a>
        ))}
      </div>
    </PageShell>
  )
}


/**
 * Storage across every dropbox. Worth showing because the per-dropbox quota
 * caps what one recipient can send, but nothing caps the sum — the number that
 * actually decides when the bucket bill arrives is this one.
 */
function StorageSummary({ totals }: { totals: Totals }) {
  return (
    <div className="card mb-6 p-5 sm:p-6">
      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label="Stored" value={formatBytes(totals.usedBytes)} />
        <Stat
          label="Files"
          value={String(totals.files)}
          hint={totals.thumbnails > 0 ? `+ ${totals.thumbnails} previews` : undefined}
        />
        <Stat label="Dropboxes" value={String(totals.dropboxes)} />
        <Stat
          label="Submitted"
          value={String(totals.readyDropboxes)}
          hint={totals.expiredDropboxes > 0 ? `${totals.expiredDropboxes} expired` : undefined}
        />
      </dl>
      {totals.allocatedBytes > 0 && (
        <p className="mt-4 text-xs text-zinc-500">
          {formatBytes(totals.allocatedBytes)} promised across all quotas — the ceiling if every
          dropbox were filled to its limit.
        </p>
      )}
    </div>
  )
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <dt className="text-xs font-medium text-zinc-500">{label}</dt>
      <dd className="mt-0.5 text-xl font-semibold tabular-nums">{value}</dd>
      {hint && <dd className="text-xs text-zinc-400">{hint}</dd>}
    </div>
  )
}
